import { BaseModel, type RelationConfig } from './base';
import { dateYMDRelation, type DateYMD } from '../lib/dates';

import { CustomField } from './customField';
import { CustomerCreditCard } from './customerCreditCard';
import { CustomerNote } from './customerNote';
import { GiftCard } from './giftCard';
import { Photo } from './photo';
import { Segment } from './segment';

// Enums

export enum CustomerGender {
  none = '',
  female = 'F',
  male = 'M',
  nonBinary = 'NB',
}

export const CustomerGenderLabels: Record<CustomerGender, string> = {
  [CustomerGender.none]: 'customer.gender.none',
  [CustomerGender.female]: 'customer.gender.female',
  [CustomerGender.male]: 'customer.gender.male',
  [CustomerGender.nonBinary]: 'customer.gender.nonBinary',
};

export enum CustomerNotifications {
  none = 0,
  all = 1,
  email = 2,
  sms = 3,
  emailPreferred = 4,
  smsPreferred = 5,
}

export const CustomerNotificationsLabels: Record<CustomerNotifications, string> = {
  [CustomerNotifications.none]: 'general.none',
  [CustomerNotifications.all]: 'general.all',
  [CustomerNotifications.email]: 'general.email',
  [CustomerNotifications.sms]: 'general.sms',
  [CustomerNotifications.emailPreferred]: 'customer.filters.emailPreferred',
  [CustomerNotifications.smsPreferred]: 'customer.filters.smsPreferred',
};

export enum CustomerMassEmail {
  subscribed = 0,
  unsubscribed = 1,
}

export const CustomerMassEmailLabels: Record<CustomerMassEmail, string> = {
  [CustomerMassEmail.subscribed]: 'general.subscribed',
  [CustomerMassEmail.unsubscribed]: 'general.unsubscribed',
};

export enum CustomerMassSMS {
  subscribed = 0,
  unsubscribed = 1,
}

export const CustomerMassSMSLabels: Record<CustomerMassSMS, string> = {
  [CustomerMassSMS.subscribed]: 'general.subscribed',
  [CustomerMassSMS.unsubscribed]: 'general.unsubscribed',
};

export const CustomerPrivacyConsent = {
  notObtained: null,
  accepted: true,
  declined: false,
} as const;

export type CustomerPrivacyConsent = typeof CustomerPrivacyConsent[keyof typeof CustomerPrivacyConsent];

export const CustomerPrivacyConsentLabels: Record<string, string> = {
  null: 'customer.consent.notObtained',
  true: 'customer.consent.accepted',
  false: 'customer.consent.declined',
};

export const CustomerMarketingConsent = {
  notObtained: null,
  accepted: true,
  declined: false,
} as const;

export type CustomerMarketingConsent = typeof CustomerMarketingConsent[keyof typeof CustomerMarketingConsent];

export const CustomerMarketingConsentLabels: Record<string, string> = {
  null: 'customer.consent.notObtained',
  true: 'customer.consent.accepted',
  false: 'customer.consent.declined',
};

export enum CombinedNoteType {
  customerNote = 'customerNote',
  reservation = 'reservation',
}

export const CombinedNoteTypeLabels: Record<CombinedNoteType, string> = {
  [CombinedNoteType.customerNote]: 'general.customer',
  [CombinedNoteType.reservation]: 'general.appointment',
};

export enum CustomerDeleted {
  notDeleted = 0,
  archived = 1,
  deleted = 2,
}

export const CustomerDeletedLabels: Record<CustomerDeleted, string> = {
  [CustomerDeleted.notDeleted]: 'customer.filters.notDeleted',
  [CustomerDeleted.archived]: 'general.archived',
  [CustomerDeleted.deleted]: 'general.deleted',
};

export enum CustomerCreditTransactionType {
  manual = 0,
  purchase = 1,
  payment = 2,
  refund = 3,
}

export const CustomerCreditTransactionTypeLabels: Record<CustomerCreditTransactionType, string> = {
  [CustomerCreditTransactionType.manual]: 'general.manual',
  [CustomerCreditTransactionType.purchase]: 'general.purchase',
  [CustomerCreditTransactionType.payment]: 'general.payment',
  [CustomerCreditTransactionType.refund]: 'general.refund',
};

export enum LoyaltyTransactionType {
  manual = 0,
  earned = 1,
  payment = 2,
  refund = 3,
}

export const LoyaltyTransactionTypeLabels: Record<LoyaltyTransactionType, string> = {
  [LoyaltyTransactionType.manual]: 'general.manual',
  [LoyaltyTransactionType.earned]: 'general.earned',
  [LoyaltyTransactionType.payment]: 'general.payment',
  [LoyaltyTransactionType.refund]: 'general.refund',
};

export enum CustomerVerifiedVia {
  driverLicense = 1,
  passport = 2,
  idCard = 3,
  other = 4,
  sight = 5,
}

export const CustomerVerifiedViaLabels: Record<CustomerVerifiedVia, string> = {
  [CustomerVerifiedVia.driverLicense]: 'customer.verification.driverLicense',
  [CustomerVerifiedVia.passport]: 'customer.verification.passport',
  [CustomerVerifiedVia.idCard]: 'customer.verification.idCard',
  [CustomerVerifiedVia.other]: 'general.other',
  [CustomerVerifiedVia.sight]: 'customer.verification.sight',
};

// Helper Classes

export class CustomerCombinedNote extends BaseModel {
  customerNote!: CustomerNote | null;
  type!: CombinedNoteType;
  content!: string;

  static override relations(): RelationConfig {
    return {
      customerNote: { type: CustomerNote },
    };
  }

  get isTypeCustomerNote(): boolean {
    return this.type === CombinedNoteType.customerNote;
  }

  get isTypeReservation(): boolean {
    return this.type === CombinedNoteType.reservation;
  }
}

export class CustomerPaymentMethods extends BaseModel {
  giftCards!: GiftCard[];
  creditCards!: CustomerCreditCard[];
  creditBalance!: number;
  loyaltyPointBalanceInCurrency!: number;

  static override relations(): RelationConfig {
    return {
      giftCards: { type: GiftCard },
      creditCards: { type: CustomerCreditCard },
    };
  }
}

export class CustomerCardsPasses extends BaseModel {
  giftCards!: GiftCard[];
  creditCards!: CustomerCreditCard[];

  static override relations(): RelationConfig {
    return {
      giftCards: { type: GiftCard },
      creditCards: { type: CustomerCreditCard },
    };
  }
}

export class CustomerCreditTransaction extends BaseModel {
  type!: CustomerCreditTransactionType;
  amount!: number;
}

export class LoyaltyTransaction extends BaseModel {
  type!: LoyaltyTransactionType;
  points!: number;
}

export class ProductHistory extends BaseModel {}

export class CustomerStats extends BaseModel {}

// Main Model

export class Customer extends BaseModel {
  idCustomer!: number;
  id!: number;
  firstName!: string;
  lastName!: string;
  displayName!: string;
  email!: string | null;
  phone!: string | null;
  homePhone!: string | null;
  company!: string | null;
  address!: string | null;
  address2!: string | null;
  city!: string | null;
  suburb!: string | null;
  region!: string | null;
  postcode!: string | null;
  country!: string | null;
  dateOfBirth!: DateYMD | null;
  gender!: CustomerGender;
  sendNotifications!: CustomerNotifications;
  consentAccepted!: CustomerPrivacyConsent;
  marketingConsentAccepted!: CustomerMarketingConsent;
  massEmailUnsubscribed!: CustomerMassEmail;
  massSMSUnsubscribed!: CustomerMassSMS;
  deleted!: CustomerDeleted;
  allowOnlineBooking!: boolean;
  creditBalance!: number;
  loyaltyPointBalance!: number;
  emailBounced!: boolean;
  profilePhoto!: Photo | null;
  alerts!: CustomerNote[];
  customerNotes!: CustomerNote[];
  customFields!: CustomField[];
  segments!: Segment[];
  stats!: CustomerStats | null;

  static override relations(): RelationConfig {
    return {
      alerts: { type: CustomerNote },
      customerNotes: { type: CustomerNote },
      customFields: { type: CustomField },
      dateOfBirth: dateYMDRelation,
      profilePhoto: { type: Photo },
      stats: { type: CustomerStats },
      segments: { type: Segment },
    };
  }

  override objectID(): string | null {
    return (this.idCustomer || this.id)?.toString() ?? null;
  }

  get addressLabel(): string | null {
    const { address, address2, city, country, postcode, region, suburb } = this;
    const items = [address, address2, city, country, postcode, region, suburb];
    let label = '';

    for (const item of items) {
      if (item && item.length) {
        label += `${item} `;
      }
    }

    return label.length ? label.trim() : null;
  }

  get image(): string | null {
    if (this.profilePhoto) {
      return (this.profilePhoto as any).imageUrls?.square ?? null;
    }
    return null;
  }

  get initials(): string {
    return (
      (this.firstName ? this.firstName.charAt(0) : '') +
      (this.lastName ? this.lastName.charAt(0) : '')
    ).toUpperCase();
  }

  get isCustomerPrivacyConsentAccepted(): boolean {
    return this.consentAccepted === CustomerPrivacyConsent.accepted;
  }

  get isArchived(): boolean {
    return this.deleted === CustomerDeleted.archived;
  }

  get isDeleted(): boolean {
    return this.deleted === CustomerDeleted.deleted;
  }

  get age(): number | null {
    const dobValue = (this.dateOfBirth as any)?.value;
    if (!dobValue) return null;

    const str = dobValue.toString();
    const year = Number(str.substring(0, 4));
    const month = Number(str.substring(4, 6)) - 1;
    const day = Number(str.substring(6, 8));
    const dob = new Date(year, month, day);

    if (isNaN(dob.getTime())) return null;

    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();

    const isBirthdayPassedThisYear =
      today.getMonth() > dob.getMonth() ||
      (today.getMonth() === dob.getMonth() && today.getDate() >= dob.getDate());

    if (!isBirthdayPassedThisYear) {
      age -= 1;
    }

    return age;
  }

  get isBirthdayToday(): boolean {
    const dobValue = (this.dateOfBirth as any)?.value;
    if (!dobValue) return false;

    const str = dobValue.toString();
    const month = Number(str.substring(4, 6)) - 1;
    const day = Number(str.substring(6, 8));

    const today = new Date();
    return today.getMonth() === month && today.getDate() === day;
  }
}
